"use client";

import type { MagnusUIMessage } from "@bg-bgi/magnus-sdk";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { format, isSameYear, isToday, isYesterday } from "date-fns";
import { useMounted } from "./use-mounted";

function formatTimestampShort(date: Date): string {
  if (isToday(date)) {
    return format(date, "h:mm a");
  }
  if (isYesterday(date)) {
    return "Yesterday";
  }
  if (isSameYear(date, new Date())) {
    return format(date, "MMM d");
  }
  return format(date, "MMM d, yyyy");
}

function formatTimestampFull(date: Date): string {
  return format(date, "MMM d, yyyy, h:mm a");
}

const TIMESTAMP_CLASS =
  "cursor-default select-none px-2 text-muted-foreground text-xs tabular-nums";

/**
 * Hover-row timestamp: a terse label ("3:35 PM", "Yesterday", "May 23") with
 * the full date in a tooltip and for screen readers.
 *
 * Formatting resolves against the browser's timezone and clock, so it is
 * gated on mount: rendering it during SSR (UTC) produced per-message
 * hydration mismatches (React #418) that cascaded into update-depth errors
 * on long streamed transcripts.
 */
export function MessageTimestamp({ createdAt }: { createdAt: Date }) {
  const mounted = useMounted();

  if (!mounted) {
    return <span className={TIMESTAMP_CLASS} />;
  }

  const short = formatTimestampShort(createdAt);
  const full = formatTimestampFull(createdAt);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span className={TIMESTAMP_CLASS}>
              <span aria-hidden="true">{short}</span>
              <span className="sr-only">{full}</span>
            </span>
          }
        />
        <TooltipContent>
          <p>{full}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Just-sent rows arrive without a persisted `createdAt`; their first-seen
// time (≈ the send time) is cached so the label doesn't tick with the clock.
// The persisted value supersedes it on the next load. Module-level, so it is
// capped (oldest first) to stay bounded across a long-lived tab.
const FIRST_SEEN_CAP = 500;
const firstSeenAtById = new Map<string, Date>();

/**
 * A message's creation time: the persisted top-level `createdAt` when the
 * row carries one, else the cached first time this id was seen.
 */
export function getMessageCreatedAt(message: { id: string }): Date {
  const raw = (message as { createdAt?: Date | string }).createdAt;
  if (raw) {
    const date = raw instanceof Date ? raw : new Date(raw);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }
  const existing = firstSeenAtById.get(message.id);
  if (existing) {
    return existing;
  }
  const now = new Date();
  firstSeenAtById.set(message.id, now);
  if (firstSeenAtById.size > FIRST_SEEN_CAP) {
    const oldest = firstSeenAtById.keys().next().value;
    if (oldest !== undefined) {
      firstSeenAtById.delete(oldest);
    }
  }
  return now;
}

/**
 * The block's default `renderMessageMeta`: a timestamp on user rows only —
 * the assistant turn that follows is implicitly dated by the one before it.
 */
export function renderUserMessageTimestamp(message: MagnusUIMessage) {
  if (message.role !== "user") {
    return null;
  }
  return <MessageTimestamp createdAt={getMessageCreatedAt(message)} />;
}
